import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import ProductItem from '../components/ProductItem';
import LightBox from './LightBox';
import { Product, PurchaseItem, Variant } from '../interfaces';
import RightIcon from '/asset/line-angle-right-icon.svg';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils';
import ShoppingBasket from '/asset/shopping-basket.svg';
import EyeIcon from '/asset/eye.svg';
import { updatePurchaseCartAsync } from '../store/purchaseSlice';
import { openLightbox, closeLightbox } from '../store/productSlice';
import { toast } from 'react-toastify';

const COLOR_CSS_MAP: Record<string, string> = {
  black: '#000000',
  white: '#FFFFFF',
  red: '#EF4444',
  blue: '#3B82F6',
  green: '#22C55E',
  gray: '#9CA3AF',
  grey: '#9CA3AF',
  silver: '#C0C0C0',
  gold: '#F59E0B',
  yellow: '#EAB308',
  orange: '#F97316',
  pink: '#EC4899',
  purple: '#8B5CF6',
  brown: '#92400E',
  navy: '#1E3A8A',
  teal: '#14B8A6',
  cyan: '#06B6D4',
  magenta: '#D946EF',
  lime: '#84CC16',
  maroon: '#7F1D1D',
  violet: '#7C3AED',
  indigo: '#4F46E5',
  beige: '#D4B896',
  cream: '#FFFDD0',
  rose: '#F43F5E',
  coral: '#F87171',
  lavender: '#C4B5FD',
  mint: '#6EE7B7',
  turquoise: '#2DD4BF',
  charcoal: '#374151',
  bronze: '#CD7F32',
  titanium: '#9CA3AF',
  graphite: '#4B5563',
  phantom: '#1F2937',
  starlight: '#F3F4F6',
  natural: '#D6C9A8',
  space: '#111827',
};

function getColorCss(colorName: string): string {
  const normalized = colorName.toLowerCase().trim();
  if (COLOR_CSS_MAP[normalized]) return COLOR_CSS_MAP[normalized];
  for (const [key, css] of Object.entries(COLOR_CSS_MAP)) {
    if (normalized.includes(key)) return css;
  }
  return colorName;
}

function parseVariantAttrs(variantKey: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  variantKey.split(',').forEach((pair) => {
    const colonIdx = pair.indexOf(':');
    if (colonIdx > -1) {
      const key = pair.slice(0, colonIdx).trim();
      const value = pair.slice(colonIdx + 1).trim();
      if (key) attrs[key] = value;
    }
  });
  return attrs;
}

export default function ProductDetails({ productId }: { productId: string }) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const products = useSelector((state: RootState) => state.products.products);
  const { storeId } = useSelector((state: RootState) => state.setup);
  const purchaseData = useSelector(
    (state: RootState) => state.purchase.purchase
  );
  const isLightboxOpen = useSelector(
    (state: RootState) => state.products.isLightboxOpen
  );

  const [activeProducts, setActiveProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product[] | null>(
    null
  );
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    null
  );
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const [activeImageSrc, setActiveImageSrc] = useState<string | null>(null);
  const [cart, setCart] = useState<PurchaseItem[]>([]);
  const [itemInCart, setItemInCart] = useState<PurchaseItem | null>(null);
  const [selectedOptionlessVariantId, setSelectedOptionlessVariantId] =
    useState<number | null>(null);

  const selectedProductItem = selectedProduct?.[0] ?? null;

  const galleryImages = useMemo<string[]>(() => {
    if (!selectedProductItem) return [];
    const images = selectedProductItem.images ?? [];
    const cover = selectedProductItem.coverImage;
    if (cover && !images.includes(cover)) return [cover, ...images];
    return images.length ? images : cover ? [cover] : [];
  }, [selectedProductItem]);

  const activeImageIndex = Math.max(
    0,
    galleryImages.indexOf(activeImageSrc ?? '')
  );

  const showPrevImage = useCallback(() => {
    if (galleryImages.length < 2) return;
    const prevIndex =
      (activeImageIndex - 1 + galleryImages.length) % galleryImages.length;
    setActiveImageSrc(galleryImages[prevIndex]);
  }, [galleryImages, activeImageIndex]);

  const showNextImage = useCallback(() => {
    if (galleryImages.length < 2) return;
    const nextIndex = (activeImageIndex + 1) % galleryImages.length;
    setActiveImageSrc(galleryImages[nextIndex]);
  }, [galleryImages, activeImageIndex]);

  const parsedVariants = useMemo(() => {
    return (selectedProduct?.[0]?.variants ?? []).map((variant) => ({
      variant,
      attrs: parseVariantAttrs(variant.variantKey),
    }));
  }, [selectedProduct]);

  const dimensions = useMemo(() => {
    const dims: Record<string, string[]> = {};
    parsedVariants.forEach(({ attrs }) => {
      Object.entries(attrs).forEach(([key, value]) => {
        if (!dims[key]) dims[key] = [];
        if (!dims[key].includes(value)) dims[key].push(value);
      });
    });
    return dims;
  }, [parsedVariants]);

  const optionNamesOrderedByPosition = useMemo(() => {
    const options = selectedProduct?.[0]?.options ?? [];
    return [...options]
      .sort((a, b) => a.position - b.position)
      .map((option) => option.name.trim());
  }, [selectedProduct]);

  const orderedDimensionKeys = useMemo(() => {
    const nameToIndex = new Map(
      optionNamesOrderedByPosition.map((name, idx) => [name, idx])
    );

    return Object.keys(dimensions).sort((a, b) => {
      const aIndex = nameToIndex.get(a);
      const bIndex = nameToIndex.get(b);
      if (aIndex === undefined && bIndex === undefined) return 0;
      if (aIndex === undefined) return 1;
      if (bIndex === undefined) return -1;
      return aIndex - bIndex;
    });
  }, [dimensions, optionNamesOrderedByPosition]);

  const hasDimensions = Object.keys(dimensions).length > 0;

  const optionlessVariants = useMemo<Variant[]>(() => {
    return parsedVariants
      .filter(({ attrs }) => Object.keys(attrs).length === 0)
      .map(({ variant }) => variant);
  }, [parsedVariants]);

  const isOptionlessSelected = selectedOptionlessVariantId !== null;

  const selectedVariant = useMemo<Variant | null>(() => {
    return (
      parsedVariants.find(({ variant }) => variant.id === selectedVariantId)
        ?.variant ?? null
    );
  }, [parsedVariants, selectedVariantId]);

  const isValueAvailable = useCallback(
    (dimKey: string, dimValue: string) => {
      const testAttrs = { ...selectedAttributes, [dimKey]: dimValue };
      return parsedVariants.some(({ attrs }) =>
        Object.entries(testAttrs).every(([key, value]) => attrs[key] === value)
      );
    },
    [parsedVariants, selectedAttributes]
  );

  const handleOptionlessVariantCheckbox = (
    variant: Variant,
    checked: boolean
  ) => {
    if (checked) {
      setSelectedOptionlessVariantId(variant.id);
      setSelectedVariantId(variant.id);
      setSelectedAttributes({});
    } else {
      setSelectedOptionlessVariantId(null);
    }
  };

  const handleAttributeSelect = (dimKey: string, dimValue: string) => {
    setSelectedOptionlessVariantId(null);
    const newAttrs = { ...selectedAttributes, [dimKey]: dimValue };
    setSelectedAttributes(newAttrs);
    const matched = parsedVariants.find(({ attrs }) =>
      Object.entries(newAttrs).every(([key, value]) => attrs[key] === value)
    );
    if (matched) setSelectedVariantId(matched.variant.id);
  };

  const upsertSelectedVariantQuantity = (nextQuantity: number) => {
    if (!selectedVariant || !selectedProductItem) return;

    const thisCart = [...cart];
    const index = thisCart.findIndex(
      (item) => item.productVariantId === selectedVariant.id
    );

    if (index >= 0) {
      if (nextQuantity > 0) {
        thisCart[index] = { ...thisCart[index], quantity: nextQuantity };
      } else {
        thisCart.splice(index, 1);
      }
    } else if (nextQuantity > 0) {
      thisCart.push({
        unitPrice: selectedVariant.unitPrice,
        quantity: nextQuantity,
        code: selectedProductItem.code,
        name: selectedProductItem.name,
        rewardsValue: 0,
        productVariantId: selectedVariant.id,
        paymentStatus: '',
        specialRequest: '',
        image:
          selectedProductItem.images?.[0] ||
          selectedProductItem.coverImage ||
          '',
        description: selectedProductItem.description,
      });
    }

    dispatch(
      updatePurchaseCartAsync({
        CartLineItems: [...thisCart],
        purchaseCode: '',
      })
    );
  };

  const addSelectedVariantToCart = (quantityToAdd: number) => {
    if (!selectedVariant || quantityToAdd <= 0) return;
    const existingQuantity =
      cart.find((item) => item.productVariantId === selectedVariant.id)
        ?.quantity ?? 0;
    upsertSelectedVariantQuantity(existingQuantity + quantityToAdd);
  };

  const removeSelectedVariantFromCart = (quantityToRemove: number) => {
    if (!selectedVariant || quantityToRemove <= 0) return;
    const existingQuantity =
      cart.find((item) => item.productVariantId === selectedVariant.id)
        ?.quantity ?? 0;
    upsertSelectedVariantQuantity(existingQuantity - quantityToRemove);
  };

  const handleBuyNow = () => {
    if (!selectedVariant) return;
    if (itemInCart && itemInCart.quantity === 0) {
      addSelectedVariantToCart(1);
    }
    navigate(storeId ? `/${storeId}/cart` : `/cart`);
  };

  useEffect(() => {
    setSelectedVariantId(null);
    setSelectedAttributes({});
    const selectedProduct_ = products.filter(
      (product) => product.id === Number(productId)
    );
    setSelectedProduct(selectedProduct_);
    const activeProduct = products
      .filter((product) => product.isActive)
      .sort(() => 0.5 - Math.random())
      .slice(0, 7);
    setActiveImageSrc(selectedProduct_[0]?.coverImage || null);
    setActiveProducts(activeProduct);

    const firstVariant = selectedProduct_[0]?.variants[0];
    if (firstVariant) {
      const firstAttrs = parseVariantAttrs(firstVariant.variantKey);
      setSelectedVariantId(firstVariant.id);
      setSelectedAttributes(firstAttrs);
      setSelectedOptionlessVariantId(
        Object.keys(firstAttrs).length === 0 ? firstVariant.id : null
      );
    } else {
      setSelectedOptionlessVariantId(null);
    }
  }, [products, productId]);

  useEffect(() => {
    setCart(purchaseData?.purchases ?? []);
  }, [purchaseData]);

  useEffect(() => {
    const item =
      cart.find((item) => item.productVariantId === selectedVariant?.id) ??
      null;
    setItemInCart(item);
  }, [cart, selectedVariant]);

  return (
    <div
      className="w-full
    mt-[30px] flex flex-col
    relative z-[300]"
    >
      {!isLightboxOpen && (
        <div
          className="w-full min-h-[200px]
      px-[10px] pb-[100px] md:px-[50px]"
        >
          {selectedProduct && selectedProduct.length > 0 && (
            <div className="md:flex md:gap-[30px]">
              {/* Product images */}
              <div className="min-h-[200px] flex-1 p-[0] md:w-[50%]">
                <div className="mb-[5px] relative">
                  <img
                    src={activeImageSrc || selectedProduct[0].coverImage}
                    alt={selectedProduct[0].name}
                    className="w-full object-contain
                  h-[300px] md:h-[400px] rounded-md border-[1px] border-gray-100"
                  />
                  <button
                    type="button"
                    title="View full image"
                    aria-label="View full image"
                    onClick={() => dispatch(openLightbox())}
                    className="p-0 opacity-[90%] absolute 
                  bottom-[10px] right-[10px] 
                  w-[30px] h-[30px] rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md cursor-pointer"
                  >
                    <img
                      src={EyeIcon}
                      className="w-[15px] h-[15px]"
                      alt=""
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <div className="flex gap-[10px] p-[2px] w-full h-[100px] mt-[10px] overflow-x-auto hide-scrollbar">
                  {selectedProduct[0].images.map((image, index) => (
                    <div
                      key={index}
                      className="bg-gray-100 w-[96px] h-[96px] border-[2px] cursor-pointer rounded-md shrink-0"
                      onClick={() => setActiveImageSrc(image)}
                    >
                      <img
                        src={image}
                        className={`w-full h-full object-cover rounded-md ${
                          activeImageSrc === image
                            ? 'border-[1px] border-secondary-500'
                            : 'border-[1px] border-gray-100'
                        }`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Product info */}
              <div
                className="
            w-full min-h-[200px] flex-1 p-[20px] md:p-0"
              >
                <div />
                <div className="text-black capitalize font-semibold text-[18px] min-h-[50px]">
                  {selectedProduct[0].name}
                </div>
                <div className="text-black text-[14px] mt-[10px]">
                  {selectedProduct[0].description}
                </div>

                <div className="bottom-1 w-full">
                  <div className="font-dmsans font-vold text-secondary-500 font-[600] text-[26px] py-[10px] border-b-[1px] border-secondary-100">
                    {selectedVariant !== null &&
                    selectedProduct[0]?.variants.length > 0
                      ? formatCurrency(selectedVariant.unitPrice)
                      : formatCurrency(
                          selectedProduct[0]?.variants[0]?.unitPrice
                        )}
                  </div>

                  {/* Variant selectors */}
                  {selectedProduct[0].variants.length > 1 && (
                    <div className="mt-[20px]">
                      {optionlessVariants.length > 0 && (
                        <div className="flex flex-col gap-[10px] mb-[16px]">
                          {optionlessVariants.map((variant) => (
                            <label
                              key={variant.id}
                              className="flex items-center gap-[8px] cursor-pointer select-none w-fit"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  selectedOptionlessVariantId === variant.id
                                }
                                onChange={(e) =>
                                  handleOptionlessVariantCheckbox(
                                    variant,
                                    e.target.checked
                                  )
                                }
                                className="w-[16px] h-[16px] accent-secondary-500 cursor-pointer !bg-white"
                              />
                              <span className="text-[13px] text-gray-700 font-medium">
                                {variant.variantKey}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}

                      {hasDimensions &&
                        orderedDimensionKeys.map((dimKey) => {
                          const dimValues = dimensions[dimKey];
                          return (
                            <div key={dimKey} className="mb-[18px]">
                              <div className="text-gray-700 text-[13px] mb-[10px] font-medium flex items-center gap-[6px]">
                                {dimKey}
                                {dimKey.toLowerCase() === 'color' &&
                                  selectedAttributes[dimKey] && (
                                    <span className="text-gray-400 font-normal">
                                      — {selectedAttributes[dimKey]}
                                    </span>
                                  )}
                              </div>

                              {dimKey.toLowerCase() === 'color' ? (
                                <div className="flex gap-[8px] flex-wrap">
                                  {dimValues.map((value) => {
                                    const isSelected =
                                      selectedAttributes[dimKey] === value;
                                    const isAvailable = isValueAvailable(
                                      dimKey,
                                      value
                                    );
                                    const cssColor = getColorCss(value);
                                    return (
                                      <button
                                        key={value}
                                        type="button"
                                        title={value}
                                        disabled={
                                          !isAvailable || isOptionlessSelected
                                        }
                                        onClick={() =>
                                          handleAttributeSelect(dimKey, value)
                                        }
                                        className={`
                                      w-[42px] h-[42px]
                                      flex items-center justify-center
                                      border-[6px] transition-all !p-0
                                      rounded-full
                                      ${
                                        isSelected
                                          ? 'border-black'
                                          : 'border-white'
                                      }
                                      ${
                                        !isAvailable || isOptionlessSelected
                                          ? 'opacity-40 cursor-not-allowed'
                                          : 'cursor-pointer hover:border-secondary-100'
                                      }
                                    `}
                                      >
                                        <div
                                          className="w-full h-full rounded-full"
                                          style={{
                                            backgroundColor: cssColor
                                              ? cssColor
                                              : 'yellow',
                                          }}
                                        />
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="relative pg-pink-300">
                                  <select
                                    value={selectedAttributes[dimKey] ?? ''}
                                    onChange={(e) =>
                                      handleAttributeSelect(
                                        dimKey,
                                        e.target.value
                                      )
                                    }
                                    disabled={isOptionlessSelected}
                                    className={`w-full h-[48px] px-[12px] pr-[40px] rounded-md border border-secondary-200 text-[14px] 
                                  outline-none focus:border-secondary-500 bg-white appearance-none text-gray-800 ${isOptionlessSelected ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                                  >
                                    {dimValues.map((value) => {
                                      if (!value) return null;
                                      const isAvailable = isValueAvailable(
                                        dimKey,
                                        value
                                      );
                                      return (
                                        <option
                                          key={value}
                                          value={value}
                                          disabled={!isAvailable}
                                        >
                                          {value}
                                          {!isAvailable ? ' (unavailable)' : ''}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <div className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 text-gray-500">
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                    >
                                      <path
                                        d="M4 6l4 4 4-4"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {/* Cart controls */}
                  <div className="mt-[20px] lg:flex lg:gap-[20px]">
                    <div
                      className="w-full lg:w-[142px] p-1 flex bg-white rounded-md 
                  border-[2px] border-solid border-gray-200"
                    >
                      <button
                        type="button"
                        className={`bg-white w-[30px] 
                      text-center rounded-[5px] flex items-center 
                      justify-center font-semibold cursor-pointer 
                      hover:bg-secondary-100 
                      ${Number(itemInCart?.quantity) > 0 ? 'text-[#191C1F]' : 'text-gray-200'}`}
                        onClick={() => {
                          if (Number(itemInCart?.quantity) > 0) {
                            removeSelectedVariantFromCart(1);
                          } else {
                            toast.error('Item is not in cart');
                          }
                        }}
                      >
                        -
                      </button>
                      <div
                        className="flex-1 text-[#191C1F] text-[14px] 
                    flex items-center justify-center"
                      >
                        {itemInCart ? itemInCart.quantity : '0'}
                      </div>
                      <button
                        type="button"
                        className="bg-white text-[#191C1F] w-[30px] 
                      text-center rounded-[5px] flex items-center 
                      justify-center font-semibold cursor-pointer 
                      hover:bg-secondary-100"
                        onClick={() => addSelectedVariantToCart(1)}
                      >
                        +
                      </button>
                    </div>
                    <div
                      className="flex flex-col md:flex-row flex-1 gap-[20px] 
                  mt-[20px] lg:mt-0"
                    >
                      <button
                        type="button"
                        className=" bg-secondary-500 flex-1 rounded-md cursor-pointer border-[2px] border-solid border-secondary-500 hover:bg-secondary-700 text-secondary-500 font-semibold text-[16px] flex items-center justify-center"
                        onClick={() => addSelectedVariantToCart(1)}
                      >
                        <div
                          className="text-white 
                      text-[12px]"
                        >
                          ADD TO CART
                        </div>
                        <div>
                          <img
                            src={ShoppingBasket}
                            className="ml-[10px] w-[20px] h-[20px]"
                          />
                        </div>
                      </button>
                      <button
                        type="button"
                        className="md:w-[142px]
                      rounded-md border-[2px] border-solid 
                      border-secondary-500 text-secondary-500 
                      font-semibold text-[14px] flex items-center 
                      justify-center bg-white cursor-pointer 
                      hover:bg-secondary-100"
                        onClick={handleBuyNow}
                      >
                        VIEW CART
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Shop other items */}
          <div className="w-full min-h-[200px] mt-[20px]">
            <div className="w-full">
              <div className="h-[60px]">
                <div className="flex gap-[10px]">
                  <div className="text-black font-semibold">
                    Shop other items
                  </div>
                  <img src={RightIcon} className="mt-[6px] w-[12px] h-[12px]" />
                </div>
              </div>
              <div className="w-full overflow-x-auto hide-scrollbar">
                <div className="flex min-w-max gap-[15px]">
                  {activeProducts?.length === 0 && (
                    <div className="flex flex-col items-center">
                      <div className="text-center text-[10px] w-full font-dmsans">
                        No product found.
                      </div>
                    </div>
                  )}
                  {activeProducts !== null &&
                    activeProducts.map((product: Product, index: number) => (
                      <ProductItem key={index} product={product} />
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLightboxOpen && (
        <LightBox
          images={galleryImages}
          activeImage={activeImageSrc || selectedProduct?.[0]?.coverImage || ''}
          onClose={() => dispatch(closeLightbox())}
          onPrev={showPrevImage}
          onNext={showNextImage}
        />
      )}
    </div>
  );
}
